import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Input, Modal, Pagination, Table } from 'react-frontier';
import { SetLoading } from '../SiteClasses';
import API from '../API';

export interface EmploymentSearchItem{
	employment_id: number,
	employment_name: string,
}

interface SearchEmploymentModalProps{
	open: boolean,
	onClose: ()=>void,
	title?: string,
	searchPlaceholder?: string,
	pageSize?: number,
	onlyActive?: boolean,
	employments: EmploymentSearchItem[],
	onSelected: (item: EmploymentSearchItem, setLoading: SetLoading)=>void,
}

var filterEmployments = (employments: EmploymentSearchItem[], query: string)=>{
	if(!query) return employments;
	var normalizedQuery = query.trim().toLowerCase();
	return employments.filter(employment =>
		employment.employment_name.toLowerCase().includes(normalizedQuery)
		|| employment.employment_id.toString() === normalizedQuery
	);
}

var SearchEmploymentModal = (props: SearchEmploymentModalProps)=>{
	var [searching, setSearching] = useState(false);
	var [searchInput, setSearchInput] = useState('');
	var [results, setResults] = useState<EmploymentSearchItem[]>([]);
	var [searchError, setSearchError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState(0);
	var [filteredCount, setFilteredCount] = useState(0);
	var [loading, setLoading] = useState(false);
	var inputRef = useRef<HTMLInputElement>(null);
	var PAGE_SIZE = props.pageSize || 10;

	var showResults = (employments: EmploymentSearchItem[], nextPage: number = 0)=>{
		var pageNumber = nextPage || 0;
		var offset = pageNumber * PAGE_SIZE;
		setPage(pageNumber);
		setFilteredCount(employments.length);
		setResults(employments.slice(offset, offset + PAGE_SIZE));
	};

	var setInitialResults = ()=>{
		showResults(props.employments || [], 0);
	};

	useEffect(()=>{
		if(!props.open) return;
		setSearchInput('');
		inputRef.current?.focus();
		setSearching(false);
		setSearchError(null);
		setSentQuery(null);
		setPage(0);
		setLoading(false);
		setInitialResults();
	}, [props.open, props.employments]);

	var searchData = (_setLoading: SetLoading, nextPage: number = 0)=>{
		if(searching) return;
		setSearching(true);
		setSearchError(null);
		setSentQuery(searchInput);

		var query = searchInput.trim();
		if(query.length < 3){
			var filtered = filterEmployments(props.employments || [], query);
			showResults(filtered, nextPage);
			setSearching(false);
			return;
		}

		API.searchEmployments(query, 0, 200).then(res=>{
			if(res.error){
				setSearchError(res.message);
				return;
			}
			var found = res.data || [];
			if(props.onlyActive){
				found = found.filter((employment: EmploymentSearchItem & { deleted?: number })=> !employment.deleted);
			}
			showResults(found, nextPage);
		}).catch(()=>{
			setSearchError('Hubo un error inesperado cargando los datos (LCL-EMP-1)');
		}).finally(()=>{
			setSearching(false);
		});
	};

	var onPageChange = (nextPage: number)=>{
		searchData(null, nextPage);
	};

	var itemSelected = (item: EmploymentSearchItem)=>{
		return ()=> props.onSelected(item, setLoading);
	}

	var hasPagination = filteredCount > PAGE_SIZE || page !== 0;
	var hasNext = filteredCount > ((page + 1) * PAGE_SIZE);

	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>{props.title || 'Seleccionar taquilla'}</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20, overflow: 'visible' }}>
			{!!props.open && (
				<Input
					ref={inputRef}
					value={searchInput}
					onChange={setSearchInput}
					placeholder={props.searchPlaceholder || 'Buscar taquilla...'}
					inputType='text'
					onSubmit={searchData}
					submitOnEnter
					button={<Button text='Buscar' onClick={searchData} loading={searching} />}
				/>
			)}
			{searching ? (
				<Header loading text='Cargando registros' containerStyle={{ marginBottom: 50, marginTop: 50 }} />
			) : searchError ? (
				<Header text='Error' subtext={searchError} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : results.length === 0 && page <= 0 ? (
				<Header
					text='Sin resultados'
					subtext={`No se encontraron registros${sentQuery ? ` con "${sentQuery}"` : ''}`}
					iconName='face-frown-open'
					style={{ marginTop: 50, marginBottom: 30 }}
					size='small'
				/>
			) : (
				<div style={{ position: 'relative' }}>
					<Table
						fitted
						selectable
						striped
						divided
						style={{ marginTop: 20 }}
						className='first bottom round'
						headers={['ID', 'Taquilla']}
						collapsingIndexes={[0]}
						footer={hasPagination && (
							<Table.Cell row colSpan={100}>
								<div style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: 15 }}>
									<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={hasNext} />
								</div>
							</Table.Cell>
						)}
					>
						{results.length === 0 ? (
							<Table.Cell row colSpan={100}>
								<Header text='Sin resultados' subtext='Esta página no tiene resultados' iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
							</Table.Cell>
						) : (
							results.map((employment, i)=>(
								<Table.Row key={`SRCH-EMP-${employment.employment_id}-${i}`} onClick={itemSelected(employment)}>
									<Table.Cell collapsing>{employment.employment_id}</Table.Cell>
									<Table.Cell>{employment.employment_name}</Table.Cell>
								</Table.Row>
							))
						)}
					</Table>
				</div>
			)}
			{loading && (
				<div style={{ position: 'absolute', width: '100%', left: 0, top: 0, height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: 8 }} className='fr big loading'></div>
			)}
		</Modal.Content>
	</Modal>
}

export default SearchEmploymentModal;
